import type { AxiosInstance } from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@libs/api/types';
import type { UserAuthenticationResponse } from '@features/auth/types';
import { useAuthStore } from '@stores/auth-store';
import { REFRESHABLE_ERROR_CODES } from '@constants/error-codes';
import * as ToastUtil from '@utils/toast-utils';

interface QueuedRequest {
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}

class TokenRefreshManager {
  private isRefreshing = false;
  private failedQueue: QueuedRequest[] = [];
  private hasShownExpirationAlert = false;
  private apiClient: AxiosInstance | null = null;

  /**
   * API 클라이언트 설정 (순환 참조 방지)
   */
  setApiClient(client: AxiosInstance): void {
    this.apiClient = client;
  }

  getIsRefreshing(): boolean {
    return this.isRefreshing;
  }

  getHasShownExpirationAlert(): boolean {
    return this.hasShownExpirationAlert;
  }

  resetExpirationAlert(): void {
    this.hasShownExpirationAlert = false;
  }

  private processQueue(error: unknown, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private showExpirationAlert(): void {
    if (!this.hasShownExpirationAlert) {
      this.hasShownExpirationAlert = true;
      ToastUtil.warning('로그인 정보가 만료되어 로그인 페이지로 이동합니다.');
      window.location.href = '/sign-in';
    }
  }

  async handle401Error(
    error: AxiosError<ApiResponse<unknown>>,
    originalRequest: InternalAxiosRequestConfig & { _isRetrying?: boolean },
  ): Promise<unknown> {
    if (!this.apiClient) {
      console.error('API Client가 설정되지 않았습니다.');
      return Promise.reject(error);
    }

    const errorCode = error.response?.data?.error?.code;

    if (!errorCode) {
      return Promise.reject(error);
    }

    // 이미 리프레시 중인 경우 큐에 추가
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      })
        .then(() => {
          if (import.meta.env.DEV) {
            console.log('🟢 [401 에러] 리프레시 완료 후 재시도');
          }
          return this.retryRequest(originalRequest);
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.log('🔴 [401 에러] 리프레시 완료 후 재시도 실패:', err);
          }
          return Promise.reject(err);
        });
    }

    // 이미 재시도 중이면 중단
    if (originalRequest._isRetrying) {
      if (import.meta.env.DEV) {
        console.log('🟡 [401 에러] 이미 재시도 중이므로 중단');
      }
      return Promise.reject(error);
    }

    // 리프레시 가능한 에러인 경우
    if (REFRESHABLE_ERROR_CODES.includes(errorCode as (typeof REFRESHABLE_ERROR_CODES)[number])) {
      return this.attemptRefresh(error, originalRequest, errorCode as (typeof REFRESHABLE_ERROR_CODES)[number]);
    }

    return Promise.reject(error);
  }

  private async attemptRefresh(
    error: AxiosError<ApiResponse<unknown>>,
    originalRequest: InternalAxiosRequestConfig & { _isRetrying?: boolean },
    errorCode: string,
  ): Promise<unknown> {
    if (!this.apiClient) {
      return Promise.reject(error);
    }

    if (import.meta.env.DEV) {
      console.log('🔵 [리프레시 가능] 에러 코드:', errorCode, '- 리프레시 토큰으로 재로그인 시도 시작');
    }

    this.isRefreshing = true;

    try {
      if (import.meta.env.DEV) {
        console.log('🟡 [리프레시 가능] refreshToken API 호출 시작');
      }

      // 순환 참조 방지를 위해 직접 refresh API 호출
      if (!this.apiClient) {
        const error = new Error('API Client가 설정되지 않았습니다.');
        this.isRefreshing = false;
        this.processQueue(error, null);
        return Promise.reject(error);
      }

      const response = await this.apiClient.post<ApiResponse<UserAuthenticationResponse>>(
        '/authenticate/refresh',
        null,
      );
      const refreshResponse = response.data;

      this.isRefreshing = false;

      if (import.meta.env.DEV) {
        console.log('🟢 [리프레시 가능] refreshToken API 응답:', {
          success: refreshResponse.success,
          hasData: !!refreshResponse.data,
          errorCode,
        });
      }

      if (refreshResponse.success && refreshResponse.data) {
        // 사용자 정보 업데이트
        if (import.meta.env.DEV) {
          console.log('🟢 [리프레시 가능] 리프레시 성공! 사용자 정보 업데이트 및 원래 요청 재시도');
        }
        useAuthStore.getState().signIn(refreshResponse.data);

        // 대기 중인 요청들 처리
        this.processQueue(null, null);

        // 원래 요청 재시도
        return this.retryRequest(originalRequest);
      } else {
        // 리프레시 실패
        // 새로운 응답 형식: { success: false, data: ~~, error: { code: "AUTH-00006", message: "..." } }
        const errorMessage =
          refreshResponse.message || refreshResponse.error?.message || '리프레시 토큰이 만료되었습니다.';

        if (import.meta.env.DEV) {
          console.log('🔴 [리프레시 가능] 리프레시 실패:', refreshResponse.error?.code, '-', errorMessage);
        }
        this.processQueue(new Error(errorMessage), null);
        useAuthStore.getState().signOut();
        this.showExpirationAlert();
        return Promise.reject(new Error(errorMessage));
      }
    } catch (refreshError) {
      this.isRefreshing = false;

      if (import.meta.env.DEV) {
        console.log('🔴 [리프레시 가능] 리프레시 토큰 호출 예외 발생:', refreshError);
      }

      this.processQueue(refreshError, null);
      useAuthStore.getState().signOut();
      this.showExpirationAlert();
      return Promise.reject(refreshError);
    }
  }

  private retryRequest(originalRequest: InternalAxiosRequestConfig & { _isRetrying?: boolean }): Promise<unknown> {
    if (!this.apiClient) {
      return Promise.reject(new Error('API Client가 설정되지 않았습니다.'));
    }

    originalRequest._isRetrying = true;
    return this.apiClient.request(originalRequest);
  }
}

export const tokenRefreshManager = new TokenRefreshManager();
